import { Breadcrumb, Button, Tabs } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { _adr } from "../../../address";
import { useDealer } from "../../../tools/api";
import { isImpersonating } from "../../../tools/impersonation";
import { Roles } from "../../../tools/services";
import { complexDigitToman, convertToJalaliWithTime, isEmpty } from "../../../tools/tools";
import IncressCharge from "../../auxiliary/incressCharge/charge";
import Loading from "../../auxiliary/loader/loading";
import { col, RangeFilter, REPORT_HINT, ReportResults } from "../../auxiliary/report/ReportPage";
import "../wallet.scss";

const ltr = (v) => <span dir="ltr">{v}</span>;

// wallettype sent to the reports: 1 dealer, 2 package, 3 charge
const WALLETS = [
	{ type: 1, key: "agentWallet", label: "کیف پول نماینده", title: "اعتبار کیف پول نماینده", amount: (u) => u?.walletAmount, visible: () => true },
	{
		type: 2,
		key: "packageWallet",
		label: "کیف پول بسته",
		title: "اعتبار کیف پول بسته",
		amount: (u) => u?.cpwalletamount,
		visible: (u) => u?.roles?.includes(Roles.Dealer_Wallet_AccessCP) && u.hasOwnProperty("cpwalletamount"),
	},
	{
		type: 3,
		key: "chargeWallet",
		label: "کیف پول شارژ",
		title: "اعتبار کیف پول شارژ",
		amount: (u) => u?.ccwalletamount,
		visible: (u) => u?.roles?.includes(Roles.Dealer_Wallet_AccessCC) && u.hasOwnProperty("ccwalletamount"),
	},
];

const TRANSACTION = {
	title: "گزارش تراکنش کیف پول",
	columns: [
		col("نام کاربری", "username"),
		col("تاریخ", "date"),
		col("شماره آپتل", "msisdn"),
		col("نوع عملیات", "actionType"),
		col("بابت", "for", { wrap: true }),
		col("مجموع برداشت (تومان)", "totalWithdrawal", { render: ltr }),
		col("مجموع واریزی (تومان)", "totalDeposit", { render: ltr }),
		col("موجودی (تومان)", "balance", {
			render: (v) => (
				<span className="result-sucess" dir="ltr">
					{v}
				</span>
			),
		}),
	],
	toRow: (item) => ({
		username: item.userName,
		date: convertToJalaliWithTime(item.dtE_OperationDate),
		msisdn: item.telNoOne,
		actionType: item.stR_WalletType,
		for: item.stR_ChargeType,
		totalWithdrawal: item.inT_ChargeAmount < 0 ? complexDigitToman(item.inT_ChargeAmount) : 0,
		totalDeposit: item.inT_ChargeAmount > 0 ? complexDigitToman(item.inT_ChargeAmount) : 0,
		balance: complexDigitToman(item.inT_WalletAmount),
	}),
	excel: { sheet: "لیست کیف پول", file: "wallet-transactions.xlsx" },
};

const SHARING = {
	title: "گزارش تراکنش تسهیم درآمد",
	columns: [
		col("شماره مشترک", "msisdn", { render: ltr }),
		col("تاریخ خرید", "simPurchaseDate"),
		col("نوع تراکنش", "purchaseType"),
		col("تاریخ تراکنش", "purchaseDate"),
		col("مبلغ تراکنش (تومان)", "purchaseAmount", { render: ltr }),
		col("سهم از تراکنش (تومان)", "shareOfPurchase", { render: ltr }),
	],
	toRow: (item) => ({
		msisdn: item.msisdn,
		simPurchaseDate: convertToJalaliWithTime(item.spPurchaseDate),
		purchaseType: item.chargeType,
		purchaseDate: convertToJalaliWithTime(item.purchaseDate),
		purchaseAmount: complexDigitToman(item.purchaseAmount) || 0,
		shareOfPurchase: complexDigitToman(item.chargeAmount) || 0,
	}),
	excel: { sheet: "سهم از فروش", file: "revenue-share.xlsx" },
};

// one wallet: balance, actions, and its reports for a date range
const WalletPanel = ({ wallet, user, onCharge }) => {
	const nav = useNavigate();
	const { queryWalletByDate, queryRevenueShareByDate } = useDealer();
	const [range, setRange] = useState(null);
	const [report, setReport] = useState("transaction");

	// the reports take wallettype/datefrom/dateto/searchconfigmodel instead of the shared report payload
	const adapt =
		(service) =>
		({ startDate, endDate, search }) =>
			service({ wallettype: wallet.type, datefrom: startDate, dateto: endDate, searchconfigmodel: search });

	// revenue share exists only for the dealer wallet
	const reports = [
		{ key: "transaction", ...TRANSACTION, query: adapt(queryWalletByDate) },
		wallet.type === 1 && user?.roles?.includes(Roles.Dealer_Wallet_Report_RevenueShare) && { key: "sharing", ...SHARING, query: adapt(queryRevenueShareByDate) },
	].filter(Boolean);
	const active = reports.find((r) => r.key === report) ?? reports[0];

	return (
		<>
			<div className="wallet-balance">
				<img className="wallet-decor" src={process.env.PUBLIC_URL + process.env.REACT_APP_PATH + "/assets/images/logo-small.png"} alt="" aria-hidden="true" />
				<div>
					<p>
						<i className="icon-wallet" /> {wallet.title}
					</p>
					<h2>
						<span dir="ltr">{complexDigitToman(wallet.amount(user))}</span> <small>تومان</small>
					</h2>
				</div>
				{/* deposit/withdraw are not allowed while impersonating a dealer */}
				{!isImpersonating() && (
					<div className="wallet-actions">
						<Button onClick={onCharge}>شارژ کیف پول</Button>
						{wallet.type === 1 && (
							<Button ghost onClick={() => nav(_adr.Root + _adr.Wallet.Index + _adr.Wallet.Withdraw.Index + _adr.Wallet.Withdraw.Change)}>
								برداشت نقدی از اعتبار
							</Button>
						)}
					</div>
				)}
			</div>

			<RangeFilter onSearch={setRange} />
			{range ? (
				<>
					{reports.length > 1 && <Tabs activeKey={active.key} onChange={setReport} items={reports.map((r) => ({ key: r.key, label: r.title }))} />}
					{/* key: each report keeps its own paging and reloads when switched to */}
					<ReportResults key={active.key} range={range} {...active} />
				</>
			) : (
				REPORT_HINT
			)}
		</>
	);
};

const WalletList = () => {
	const [t] = useTranslation("global");
	const { _currentUser } = useSelector((state) => state?.another);
	const [searchParam] = useSearchParams();
	const wallets = WALLETS.filter((w) => w.visible(_currentUser));
	const [activeKey, setActiveKey] = useState(wallets[0].key);
	const wallet = wallets.find((w) => w.key === activeKey) ?? wallets[0];

	// wallet top-up: the modal also resumes a payment when the bank redirects back here
	const [openCharge, setOpenCharge] = useState(false);
	const [localInvoiceId, setLocalInvoiceId] = useState("");
	const [behpardakht, setBehpardakht] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const bankRef = useRef(null);

	useEffect(() => {
		if (!isEmpty(searchParam.get("localInvoiceId")) || !isEmpty(searchParam.get("RefId"))) {
			setBehpardakht((beh) => ({
				...beh,
				RefId: searchParam.get("RefId") || "",
				ResCode: searchParam.get("ResCode") || "",
				SaleOrderId: searchParam.get("SaleOrderId") || "",
				SaleReferenceId: searchParam.get("SaleReferenceId") || "",
			}));
			setLocalInvoiceId(searchParam.get("localInvoiceId"));
			setOpenCharge(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// the gateway answers with an auto-posting bank form (html), or nothing when the modal is closed
	const handleCallBackCharge = (value) => {
		setIsLoading(true);
		if (value.includes("html")) {
			bankRef.current.innerHTML = value;
			document.querySelector('form[action="https://asan.shaparak.ir"]').submit();
		} else if (value.includes("form")) {
			value = value.replace(/<form([^>]*)target="_blank"([^>]*)>/gi, "<form$1$2>");
			bankRef.current.innerHTML = value;
			document.querySelector("form").submit();
		} else {
			setLocalInvoiceId("");
			setOpenCharge(false);
		}
		setIsLoading(false);
	};

	return (
		<div className="container-main container-wa">
			<div className="g-hold-title-of-page">
				<Breadcrumb className="breadcrumbs" items={[{ href: _adr.Root + _adr.Home, title: <HomeOutlined /> }, { title: t("wallet.title") }]} />
				<div className="title-page">
					<h3>{t("wallet.title")}</h3>
				</div>
				{wallets.length > 1 && <Tabs className="wallet-tabs" activeKey={wallet.key} onChange={setActiveKey} items={wallets.map(({ key, label }) => ({ key, label }))} />}
			</div>
			<div className="g-layout-content">
				<Content className="g-layout-main report-page wa-container">
					{/* key: switching wallets starts a fresh search */}
					<WalletPanel key={wallet.key} wallet={wallet} user={_currentUser} onCharge={() => setOpenCharge(true)} />
				</Content>
			</div>
			<IncressCharge
				behpardakht={behpardakht}
				open={openCharge}
				planid={process.env.REACT_APP_PLANID_CHARGE_WALLET}
				msisdn={_currentUser?.telnoone}
				price={0}
				callback={handleCallBackCharge}
				localInvoiceId={localInvoiceId}
				activePrice={true}
			/>
			<div ref={bankRef}></div>
			<Loading isOpen={isLoading} />
		</div>
	);
};

export default WalletList;
